import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Pencil, Crown, AlertTriangle } from "lucide-react";
import { PageLoader, LoadError } from "@/components/admin/LoadState";
import { TierPolicyCard } from "@/components/admin/TierPolicyCard";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useDebounced } from "@/hooks/useDebounced";
import { fmtNum, plural } from "@/lib/format";

export default function AdminTiers() {
  usePageTitle("Niveles y beneficios");
  const [tiers, setTiers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [edit, setEdit] = useState<any>(null);
  const [form, setForm] = useState<any>({ min_points: 0, color: "#D4AF37", benefits: "" });
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState<{ up: number; down: number; kept?: number } | null>(null);
  const minPoints = useDebounced(form.min_points, 400);

  const load = () => {
    setFailed(false);
    return api.get("/tiers").then((t) => setTiers(t.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Antes de guardar: cuántos clientes subirían o bajarían de nivel con el nuevo mínimo.
  useEffect(() => {
    setImpact(null);
    if (!edit || String(minPoints) === String(edit.min_points) || !/^\d+$/.test(String(minPoints))) return;
    api.get(`/admin/tiers/${encodeURIComponent(edit.name)}/impact`, { params: { min_points: minPoints } })
      .then((r) => setImpact(r.data)).catch(() => setImpact(null));
  }, [minPoints, edit]);

  const openTier = (t: any) => {
    setEdit(t);
    setForm({ min_points: t.min_points, color: t.color, benefits: t.benefits.join("\n") });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/tiers/${encodeURIComponent(edit.name)}`, {
        name: edit.name,
        min_points: Number(form.min_points),
        multiplier: edit.multiplier,
        color: form.color,
        benefits: form.benefits.split("\n").map((b: string) => b.trim()).filter(Boolean),
      });
      toast.success(`Nivel ${edit.name} actualizado`);
      setEdit(null); load();
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <PageLoader />;
  if (failed) return <LoadError what="los niveles" onRetry={load} />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-tiers-page">
      <div>
        <h1 className="text-3xl font-serif font-medium">Niveles y beneficios</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">El nivel de cada cliente depende de los puntos que ganó en el año, no de su saldo: canjear no lo baja. Aquí defines desde cuántos puntos empieza cada nivel, qué beneficios ve el cliente y qué pasa al empezar un año nuevo.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiers.map((t) => (
          <div key={t.name} className="card-obsidian p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: t.color + "22", border: `1px solid ${t.color}` }}>
                <Crown className="w-4 h-4" style={{ color: t.color }} aria-hidden="true" />
              </div>
              <div>
                <h2 className="font-serif font-medium">{t.name}</h2>
                <p className="text-xs text-slate-600">Desde {fmtNum(t.min_points)} pts</p>
              </div>
            </div>
            <ul className="text-xs text-slate-700 mb-4 list-disc pl-4 space-y-0.5">
              {t.benefits.slice(0, 4).map((b: string) => <li key={b}>{b}</li>)}
              {t.benefits.length > 4 && <li className="list-none -ml-4 text-slate-600">y {t.benefits.length - 4} más</li>}
              {t.benefits.length === 0 && <li className="list-none -ml-4 text-slate-600">Sin beneficios cargados</li>}
            </ul>
            <Button data-testid="edit-tier-button" size="sm" variant="outline" onClick={() => openTier(t)} className="w-full border-slate-200 text-slate-700 hover:bg-slate-100 h-9">
              <Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Editar nivel
            </Button>
          </div>
        ))}
      </div>

      <TierPolicyCard />

      <Dialog open={!!edit} onOpenChange={(o: boolean) => !o && setEdit(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Nivel {edit?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="tier-min" className="text-slate-700 text-sm">Desde cuántos puntos</Label>
              <Input id="tier-min" data-testid="tier-minpoints-input" type="number" min={0} value={form.min_points} onChange={(e: any) => setForm({ ...form, min_points: e.target.value })} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              <p className="text-xs text-slate-600 mt-1">Debe quedar entre el nivel anterior y el siguiente. El primer nivel siempre empieza en 0.</p>
              {impact && (impact.up > 0 || impact.down > 0) && (
                <p data-testid="tier-impact" className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm text-amber-900 flex items-start gap-1.5">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>Al guardar, {impact.up > 0 && <>{plural(impact.up, "cliente sube", "clientes suben")} de nivel</>}{impact.up > 0 && impact.down > 0 && " y "}{impact.down > 0 && <>{plural(impact.down, "cliente baja", "clientes bajan")} de nivel</>}. El cambio es inmediato.</span>
                </p>
              )}
              {impact && !!impact.kept && <p className="text-xs text-slate-600 mt-1" data-testid="tier-impact-kept">{plural(impact.kept, "cliente ya no alcanzaría", "clientes ya no alcanzarían")} este nivel con el nuevo mínimo, pero lo conservan hasta el 31 de diciembre.</p>}
              {impact && impact.up === 0 && impact.down === 0 && !impact.kept && <p className="text-xs text-slate-600 mt-1">Con este cambio ningún cliente cambia de nivel.</p>}
            </div>
            <div>
              <Label htmlFor="tier-color" className="text-slate-700 text-sm">Color del nivel</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input id="tier-color" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-11 h-11 rounded-lg bg-transparent border border-slate-200 cursor-pointer" />
                <span className="text-sm text-slate-600 font-mono">{form.color}</span>
              </div>
            </div>
            <div>
              <Label htmlFor="tier-benefits" className="text-slate-700 text-sm">Beneficios (uno por línea; los ve el cliente)</Label>
              <Textarea id="tier-benefits" data-testid="tier-benefits-input" rows={6} value={form.benefits} onChange={(e: any) => setForm({ ...form, benefits: e.target.value })} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button data-testid="save-tier-button" onClick={save} disabled={saving} className="gold-gradient-bg text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar nivel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
